/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, useMemo } from 'react';
import { Link } from '@/components/link';
import { useAuth } from '@/providers/auth';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { UserMenu } from './user-menu';

const AccountIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24" className="size-5">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
    </svg>
);

export default function UserActions(): ReactElement {
    const session = useAuth();
    const { t } = useTranslation('header');
    const { t: tAccount } = useTranslation('account');
    const isAuthenticated: boolean = useMemo(() => {
        // Check if user is authenticated (has valid token and is registered)
        return Boolean(session?.userType === 'registered' && session?.customerId);
    }, [session]);

    const accountLink = isAuthenticated ? '/account/overview' : '/login';
    const ariaLabel = isAuthenticated ? tAccount('myAccount') : t('signIn');
    const icon = <AccountIcon />;

    const trigger = (
        <Button
            variant="ghost"
            className="cursor-pointer lg:px-4 px-1 hover:bg-transparent hover:opacity-50 transition-opacity"
            asChild>
            <Link to={accountLink} aria-label={ariaLabel}>
                {icon}
            </Link>
        </Button>
    );

    return <UserMenu isAuthenticated={isAuthenticated} trigger={trigger} />;
}
